import { useAppSelector, useAppDispatch } from 'flow/hooks/useRedux';
import { fromJS, List, Map } from 'immutable';
import { updateQueueMember } from 'flow/actions/pbx-actions';
import { updateProfiles } from 'flow/actions/profile-actions';
import RouteLink from 'flow/components/ui-components/RouteLink';
import Toggle from 'flow/components/ui-components/Toggle';
import useAppContext from 'flow/hooks/useAppContext';
import useProfiles from 'flow/hooks/useProfiles';
import { selectAllQueueMembers } from 'flow/selectors/queue-selectors';
import { ExtensionKey, QueueKey } from 'flow/types/EntityKeys';
import { QueueMember } from 'flow/types/Queue';
import { Profile, QueueLoginList } from 'flow/types/Profile';
import { routes } from 'flow/types/routes';
import { TypedMap } from 'flow/types/TypedMap';
import { getQueueLoggedInValueForProfile } from 'flow/utils/profile-utils';
import { generatePath, useLocation, useParams } from 'react-router-dom';
import SettingRow from '../SettingRow';
import SettingSection from '../SettingSection';

interface Props {
    perProfile: boolean;
}

const Queues = ({ perProfile }: Props) => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();
    const { pathname } = useLocation();
    const { extensionKey } = useParams<{
        extensionKey: ExtensionKey;
    }>();
    const isAiCoworker = window.location.pathname.includes('ai-coworker');
    const { profiles } = useProfiles(extensionKey);

    const myQueueLogins = profiles.first().get('queueLoginList');
    const allQueueMembers = useAppSelector(selectAllQueueMembers);

    const handleChange = async (key: QueueKey, value: boolean) => {
        const profilesWithUpdatedQueueLogins = updateProfileListFromQueue(
            profiles,
            key,
            value,
        );
        // Filter out the key and the queueLoginList so that the back-end don't have to deal with anything else.
        const updatedProfileList = profilesWithUpdatedQueueLogins.map(
            (profile) =>
                fromJS({
                    key: profile.get('key'),
                    queueLoginList: profile.get('queueLoginList'),
                }),
        );

        dispatch(updateProfiles(extensionKey, updatedProfileList));

        const memberDTO = allQueueMembers
            .get(key)
            ?.find(
                (member: TypedMap<QueueMember>) =>
                    member.get('extensionKey') === extensionKey,
            );
        if (memberDTO) {
            const updatedMemberDTO = memberDTO.set('loggedIn', value);
            dispatch(updateQueueMember(key, updatedMemberDTO));
        }
    };

    const updateProfileListFromQueue = (
        profileList: List<TypedMap<Profile>>,
        queueKey: QueueKey,
        value: boolean,
    ) =>
        profileList.map((profile) =>
            profile.update(
                'queueLoginList',
                List<TypedMap<QueueLoginList>>(),
                (prevQueueLogins: List<TypedMap<QueueLoginList>>) => {
                    const indexOfQueueToUpdate = prevQueueLogins.findIndex(
                        (login: TypedMap<QueueLoginList>) =>
                            login.get('queueEntityKey') === queueKey,
                    );
                    if (indexOfQueueToUpdate === -1) {
                        return prevQueueLogins.push(
                            fromJS({
                                queueEntityKey: queueKey,
                                loggedIn: value,
                            }),
                        );
                    }
                    return prevQueueLogins.setIn(
                        [indexOfQueueToUpdate, 'loggedIn'],
                        value,
                    );
                },
            ),
        );

    const getDescription = (queueLogin: TypedMap<QueueLoginList>) => {
        const loggedInProfiles = myQueueLogins.reduce(
            (
                loginsPerQueue: TypedMap<Record<QueueKey, number>>,
                queueLogin: TypedMap<QueueLoginList>,
            ) => {
                const queueKey = queueLogin.get('queueEntityKey');

                const numberOfLoggedInProfiles = profiles.count((profile) =>
                    getQueueLoggedInValueForProfile(profile, queueKey),
                );
                return loginsPerQueue.set(queueKey, numberOfLoggedInProfiles);
            },
            Map(),
        );

        const queueKey = queueLogin.get('queueEntityKey');
        const enabledProfiles = loggedInProfiles.get(queueKey);

        if (enabledProfiles === 0) {
            return i18next.t('settings.off');
        }

        if (enabledProfiles === profiles.size) {
            return i18next.t('settings.on');
        }

        return i18next.t('settings.number_of_profiles', {
            activeCount: enabledProfiles,
            profileCount: profiles.size,
        });
    };

    const link = isAiCoworker
        ? routes[
              'pbx.aicoworker.extensionKey.settings.queuing-system-login.queueKey'
          ]
        : routes['settings.queue-and-chat-login.queueKey'];

    return (
        <>
            <SettingSection className="text-sm/4">
                {i18next.t('pbx.queues')}
            </SettingSection>

            {profiles
                .first()
                .get('queueLoginList')
                .sort((a, b) =>
                    a
                        .get('queueDescription')
                        .localeCompare(b.get('queueDescription')),
                )
                .map((queue: TypedMap<QueueLoginList>) => {
                    if (perProfile) {
                        return (
                            <RouteLink
                                key={queue.get('queueEntityKey')}
                                to={generatePath(link, {
                                    queueKey: queue.get('queueEntityKey'),
                                    extensionKey: extensionKey,
                                })}
                                state={{ from: pathname }}
                                description={getDescription(queue)}
                            >
                                {queue.get('queueDescription')}
                            </RouteLink>
                        );
                    }

                    return (
                        <SettingRow
                            key={queue.get('key')}
                            cta={
                                <Toggle
                                    value={queue.get('loggedIn', false)}
                                    onChange={(value) =>
                                        handleChange(
                                            queue.get('queueEntityKey'),
                                            value,
                                        )
                                    }
                                    text={queue.get('queueDescription')}
                                />
                            }
                        >
                            {queue.get('queueDescription')}
                        </SettingRow>
                    );
                })}
        </>
    );
};

export default Queues;
